<script setup lang="ts">
import { computed, defineProps } from "vue";
import { elementStageNames, tenGods, ownerOrder } from "@/constants/bazi";
import { elColor } from "@/constants/colors";
import { Hidden } from "@/types/calculators";
import AppTooltip from "@/components/AppTooltip.vue";
import { useI18n } from "vue-i18n";

const props = defineProps<{ hidden: Hidden[] }>();

const { t } = useI18n();

const groupedHidden = computed(() => {
  return ownerOrder
    .map((owner) => ({
      owner,
      label: "calculators.bazi.results.birthday-map.times." + owner,
      items: props.hidden.filter((h) => h.owner === owner),
    }))
    .filter((group) => group.items.length > 0);
});
</script>

<template>
  <table
    class="table-auto border border-gray-300 text-center align-middle w-full"
  >
    <thead
      class="bg-brown-gradient text-white text-lg leading-[24px] font-semibold"
    >
      <tr class="h-12">
        <th class="opacity-0 w-[1px]">#</th>
        <th>
          {{ t("calculators.bazi.results.birthday-map.stem.heaven-stem") }}
        </th>
        <th>
          {{ t("calculators.bazi.results.birthday-map.stem.earth-branch") }}
        </th>
        <th>
          {{ t("calculators.bazi.results.birthday-map.stem.stage") }}
        </th>
        <th>
          {{ t("calculators.bazi.results.birthday-map.stem.element") }}
        </th>
        <th>
          {{ t("calculators.bazi.results.birthday-map.stem.god") }}
        </th>
      </tr>
    </thead>
    <tbody>
      <template v-for="group in groupedHidden" :key="group.owner">
        <template v-for="(row, idx) in group.items" :key="group.owner + idx">
          <tr>
            <td
              v-if="idx === 0"
              :rowspan="group.items.length"
              class="font-semibold align-middle border px-3 py-2 text-brown"
            >
              {{ t(group.label) }}
            </td>

            <!-- Небесный ствол -->
            <td class="border px-4 py-2">
              <AppTooltip>
                <template #trigger>
                  <h4 :class="elColor[row.element]">{{ row.stem }}</h4>
                </template>
                <template #popper>
                  <div class="max-w-96">
                    <h5>{{ row.labels.stem }}</h5>
                    <div class="text-left">{{ row.tips.stem }}</div>
                  </div>
                </template>
              </AppTooltip>
            </td>

            <!-- Земная ветвь -->
            <td
              v-if="idx === 0"
              :rowspan="group.items.length"
              class="font-semibold align-middle border px-3 py-2"
            >
              <AppTooltip>
                <template #trigger>
                  <h2 class="text-[#dd6c1b]">{{ row.branch }}</h2>
                </template>
                <template #popper>
                  <div class="max-w-96">
                    <h5>{{ row.labels.branch }}</h5>
                    <div class="text-left">{{ row.tips.branch }}</div>
                  </div>
                </template>
              </AppTooltip>
            </td>

            <!-- Стадия -->
            <td class="border px-4 py-2">
              <AppTooltip>
                <template #trigger>
                  <h4
                    class="mb-0"
                    :style="`color: ${elementStageNames[row.stage]['color']}`"
                  >
                    {{ row.stage }}
                  </h4>
                </template>
                <template #popper>
                  <div class="max-w-96">
                    <h5>{{ row.labels.stage }}</h5>
                    <div class="text-left">{{ row.tips.stage }}</div>
                  </div>
                </template>
              </AppTooltip>
            </td>

            <!-- Стихия -->
            <td class="border p-2 size-10">
              <AppTooltip>
                <template #trigger>
                  <img
                    :src="
                      require(`@/assets/calculators/bazi/${row.element}.png`)
                    "
                    :alt="row.element"
                    class="py-1 size-14"
                  />
                </template>
                <template #popper>
                  <div class="max-w-96 text-left">
                    {{ row.tips.element }}
                  </div>
                </template>
              </AppTooltip>
            </td>

            <!-- Божество -->
            <td class="border px-4 py-2">
              <AppTooltip>
                <template #trigger>
                  <h4
                    class="mb-0"
                    :style="`color: ${tenGods[row.god]['color']}`"
                  >
                    {{ row.god }}
                  </h4>
                </template>
                <template #popper>
                  <div class="max-w-96">
                    <h5>{{ row.labels.god }}</h5>
                    <div class="text-left">{{ row.tips.god }}</div>
                  </div>
                </template>
              </AppTooltip>
            </td>
          </tr>
        </template>
      </template>
    </tbody>
  </table>
</template>
